import { Box, Stack, Tab, Tabs, Typography, styled } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContactsList from "./ContactsList";
import { updateSelectedPendingContact } from "../../../state/actions/contacts";

const StyledTabLabel = styled(Typography)(({ selectedTabId, tabIndex }) => ({
	fontSize: "16px",
	textTransform: "capitalize",
	fontFamily: "Figtree-Regular",
	fontWeight: 600,
	width: "100%",
	color: "#5F6265",
}));

const CustomCountChip = ({ label }) => {
  return (
    <div className="h-full w-full rounded-md bg-gray-200 px-2 py-1 text-sm font-medium text-gray-800">
      {label}
    </div>
  );
};

export const StyledTab = styled(Tab)(({ selectedTabId, tabIndex }) => ({
	border: "1px solid #f3f3fa",
	backgroundColor: selectedTabId === tabIndex ? "#fff" : "#f3f3fa",
    "&.Mui-selected": {
        backgroundColor: "#fff",
        border: '2px solid #f3f3fa',
    }
}));

const CustomTabPanel = (props) => {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ pt: 2 }}>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
};

const TabLabel = ({ selectedTabId, tabIndex, label, count }) => {
	return (
		<Stack
			flexDirection="row"
			alignItems="center"
			justifyContent="center"
			gap={1}
		>
			<StyledTabLabel selectedTabId={selectedTabId} tabIndex={tabIndex}>
				{label}
			</StyledTabLabel>
			<CustomCountChip label={count} sx={{ backgroundColor: "#ccc" }} />
		</Stack>
	);
};

function RequestedContacts(props) {
	const dispatch = useDispatch();
	const { pendingContacts, ignored: ignoredContacts } = useSelector((state) => state.contacts);
	const [sentContacts, setSentContacts] = useState([]);
	const [receivedContacts, setReceivedContacts] = useState([]);
	const [selectedTabId, setSelectedTabId] = useState(0);

	const handleTabChange = (e, newValue) => {
		setSelectedTabId(newValue);
		dispatch(updateSelectedPendingContact({}));
	};

	const { sentContactsList, receivedContactsList } = useMemo(() => {
		let sentContactsData = [],
			receivedContactsData = [];
		pendingContacts?.forEach((contact) => {
			if (
				contact.hasOwnProperty("waitingForConfirmation") &&
				contact.waitingForConfirmation
			) {
				sentContactsData.push(contact);
			} else {
				receivedContactsData.push(contact);
			}
		});
		return { sentContactsList: sentContactsData, receivedContactsList: receivedContactsData };
	}, [pendingContacts]);

	useEffect(() => {
		if (JSON.stringify(sentContactsList) !== JSON.stringify(sentContacts)) {
			setSentContacts(sentContactsList);
		} 
		if (JSON.stringify(receivedContactsList) !== JSON.stringify(receivedContacts)) {
			setReceivedContacts(receivedContactsList);
		}
	}, [sentContactsList, receivedContactsList]);

	const filteredReceivedContacts = receivedContacts.filter(contact => 
		!ignoredContacts.some(ignored => ignored.userId === contact.userId)
	);

	const receivedContactsCount = filteredReceivedContacts.length;


	return (
		<Box>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Tabs
					sx={{ borderRadius: "8px" }}
					TabIndicatorProps={{
						style: {
							backgroundColor: "#f3f3fa",
							height: "1px",
							borderRadius: "4px",
						},
					}}
					value={selectedTabId}
					textColor="#6267AD"
					onChange={handleTabChange}
					variant="fullWidth"
				>
					<StyledTab
						selectedTabId={selectedTabId}
						tabIndex={0}
						label={
							<TabLabel
								selectedTabId={selectedTabId}
								tabIndex={0}
								label="Sent"
								count={sentContacts.length}
							/>
						}
					/>
					<StyledTab
						selectedTabId={selectedTabId}
						tabIndex={1}
						label={
							<TabLabel
								selectedTabId={selectedTabId}
								tabIndex={1}
								label="Received"
								count={receivedContactsCount}
							/>
						}
					/>
				</Tabs>
			</Box>
			<CustomTabPanel value={selectedTabId} index={0}>
				<ContactsList
					contactsDataList={sentContacts}
					selectedTabId={selectedTabId}
					accept={props.accept}
					allProfileImages={props.allProfileImages}
				/>
			</CustomTabPanel>
			<CustomTabPanel value={selectedTabId} index={1}>
				<ContactsList
					contactsDataList={receivedContacts}
					selectedTabId={selectedTabId}
					ignore={props.ignore}
					accept={props.accept}
					allProfileImages={props.allProfileImages}
				/>
			</CustomTabPanel>
		</Box>
	);
}

export default React.memo(RequestedContacts);
